import React, { Fragment, ReactNode, useEffect, useRef } from 'react';
import './Dialog.less';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import Icon from '../Icon/Icon';
import useEscape from '../../../helper/Hooks';

interface DialogTypes {
    title?: string;
    description?: string | ReactNode;
    content?: ReactNode | string;
    actions?: ReactNode[];
    visible: boolean;
    children?: ReactNode | string;
    classNameContent?: string;
    closable?: boolean;
    onClose?: (opened: boolean) => void;
    isStatic?: boolean;
    fullscreen?: boolean;
    /**
     * Desktop footprint of the dialog. `default` hugs its content (the norm for forms and
     * confirmations); `wide` adopts the large near-fullscreen footprint used for dialogs that
     * host a big scrollable table (e.g. "Aufträge teilen"), matching the "Aufträge herunterladen"
     * wizard. On mobile every dialog is full-screen regardless of this prop (see Dialog.less).
     */
    size?: 'default' | 'wide';
}

const Dialog = ({
    title,
    description,
    content,
    actions,
    visible,
    children,
    classNameContent,
    closable,
    onClose,
    isStatic = true,
    fullscreen = false,
    size = 'default',
}: DialogTypes) => {
    useEscape(() => {
        if (onClose) onClose(false);
    });

    useEffect(() => {
        return () => {
            setTimeout(() => {
                let portal = document.getElementById('headlessui-portal-root');

                if (!portal) {
                    document.documentElement.removeAttribute('style');
                }
            }, 500);
        };
    }, [visible]);

    let completeButtonRef = useRef(null);

    const buttonExists = actions ? actions?.length > 0 : false;

    if (!actions || actions.length === 0) {
        actions = [<button key={'default-button'} className={'opacity-0 absolute bottom-0'}></button>];
    }

    return (
        <Transition as={Fragment} show={visible}>
            <HeadlessDialog
                static={true}
                initialFocus={completeButtonRef}
                open={visible}
                aria-expanded={visible}
                className={fullscreen ? 'dialog-fullscreen' : 'dialog'}
                onClose={() => {}}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-500"
                    // Opacity only — no scale/translate: under Tailwind 3 such a class leaves a lasting
                    // transform on the wrapper, which re-anchors the position:fixed content to the
                    // wrapper instead of the viewport (shifted camera view on re-open, qa#134).
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className={'dialog--wrapper'}>
                        <HeadlessDialog.Overlay className={'dialog--overlay'} />
                        <div className={classNames('dialog--content', { 'dialog--content-wide': size === 'wide' }, classNameContent)}>
                            {closable && (
                                <button
                                    aria-label={'Dialog schließen'}
                                    className={'absolute right-2 top-2 focus:outline-none cursor-pointer'}
                                    onClick={() => {
                                        if (onClose) {
                                            onClose(false);
                                        }
                                    }}
                                >
                                    <Icon
                                        aria={{ 'aria-hidden': true }}
                                        path={Icon.Path.mdiClose}
                                        className={'w-8 text-gray-300 hover:text-gray-200 transition'}
                                    />
                                </button>
                            )}

                            {title != null && <HeadlessDialog.Title className={'dialog--title'}>{title || ''}</HeadlessDialog.Title>}

                            <div className={'dialog--body flex flex-1 flex-col'}>
                                <HeadlessDialog.Description className={'dialog--description'}>{description}</HeadlessDialog.Description>

                                <div className={'dialog--text overflow-y-auto flex-1'}>{children || content}</div>
                            </div>

                            <div className={'flex-0'} />
                            {actions && actions.length > 0 && (
                                <div
                                    className={classNames('dialog--actions', {
                                        hidden: !buttonExists,
                                        flex: buttonExists,
                                    })}
                                >
                                    {actions}
                                </div>
                            )}

                            <button ref={completeButtonRef} className={'hidden'}></button>
                        </div>
                    </div>
                </Transition.Child>
            </HeadlessDialog>
        </Transition>
    );
};

export default Dialog;
